import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../firebase';
import { moveParticipant } from './TeamsTab';

// ── Group Flights (Mic Cup and Long Haul tours) ────────────────────────────────────────────
// Staff sort everyone who has registered into the group flight booking or
// separate flights, using the same tap-or-drag pattern as rooming and teams.
//
// Stored at requests/{id}/flights/layout as
// { assignments: { group: [participantId], separate: [participantId] } }.
// Every change saves straight away.

export const FLIGHT_BOXES = [
  { id: 'group',    name: 'Group Flights',    hint: 'Booked together on the group flight' },
  { id: 'separate', name: 'Separate Flights', hint: 'Travelling on their own flights' },
];

export function flightsLayoutRef(requestId) {
  return doc(db, 'requests', requestId, 'flights', 'layout');
}

function fullName(p) {
  return [p.firstName, p.middleNames, p.surname].filter(Boolean).join(' ') || p.email || '—';
}

/** True when the participant said on their form they're booking their own flights. */
export function booksOwnFlights(p) {
  return /independent/i.test(p?.flightArrangementMethod || '');
}

function exportFlightsSheet({ assignments, participants, request }) {
  const byId = new Map(participants.map((p) => [p.id, p]));
  const row = (box, p) => [box, fullName(p), p.travellerType || '', p.passportNumber || '', p.flightArrangementMethod || ''];
  const rows = [];
  FLIGHT_BOXES.forEach((box) => {
    (assignments[box.id] || []).forEach((pid) => { const p = byId.get(pid); if (p) rows.push(row(box.name, p)); });
  });
  const placed = new Set(Object.values(assignments).flat());
  participants.filter((p) => !placed.has(p.id)).forEach((p) => rows.push(row('Not sorted', p)));

  const ws = XLSX.utils.aoa_to_sheet([['Flights', 'Full Name', 'Traveller Type', 'Passport Number', 'Their form said'], ...rows]);
  ws['!cols'] = [18, 30, 16, 18, 24].map((wch) => ({ wch }));
  ws['!autofilter'] = { ref: `A1:E${rows.length + 1}` };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Flights');
  const name = request?.internalRef || request?.tourName || 'tour';
  XLSX.writeFile(wb, `flights-${String(name).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.xlsx`);
}

export default function GroupFlightsTab({ requestId, request }) {
  const [assignments, setAssignments] = useState({});
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // participant id being placed
  const [error, setError] = useState('');

  useEffect(() => onSnapshot(flightsLayoutRef(requestId), (snap) => {
    setAssignments(snap.exists() ? snap.data().assignments || {} : {});
    setLoading(false);
  }), [requestId]);

  useEffect(() => onSnapshot(collection(db, 'requests', requestId, 'participants'), (snap) => {
    setParticipants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }), [requestId]);

  async function place(boxId, participantId = selected) {
    if (!participantId) return;
    const next = moveParticipant(assignments, participantId, boxId);
    setAssignments(next);
    setSelected(null);
    setError('');
    try {
      await setDoc(flightsLayoutRef(requestId), { assignments: next, updatedAt: serverTimestamp() }, { merge: true });
    } catch (err) {
      console.error(err);
      setError('Could not save that change. Please try again.');
    }
  }

  const byId = useMemo(() => Object.fromEntries(participants.map((p) => [p.id, p])), [participants]);
  const sorted = useMemo(() => [...participants].sort((a, b) => fullName(a).localeCompare(fullName(b))), [participants]);
  const placedIds = new Set(Object.values(assignments).flat());
  const unsorted = sorted.filter((p) => !placedIds.has(p.id));
  const selectedP = selected ? byId[selected] : null;

  const dropProps = (boxId) => ({
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => { const id = e.dataTransfer.getData('text/plain'); if (id) place(boxId, id); },
  });

  const card = (p, { inBox } = {}) => {
    const isSel = selected === p.id;
    return (
      <div
        key={p.id}
        draggable
        onDragStart={(e) => { e.dataTransfer.setData('text/plain', p.id); setSelected(p.id); }}
        onDragEnd={() => setSelected(null)}
        onClick={(e) => { e.stopPropagation(); setSelected(isSel ? null : p.id); }}
        className={`flex items-center gap-2 px-3 py-2 border rounded-[10px] cursor-pointer select-none transition-all ${
          isSel ? 'bg-pb border-pb text-white shadow-md'
            : inBox ? 'bg-[#F9FAFB] border-[#E5E7EB] text-[#374151] hover:border-[#93C5FD]'
              : 'bg-[#EBF2FC] border-[#93C5FD] text-pb'
        }`}
        title={inBox ? 'Tap, then tap the other box to move' : 'Tap, then tap a box to place'}
      >
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold truncate">
            {fullName(p)}
            {p.travellerType && <span className={`ml-1.5 text-[10.5px] font-bold ${isSel ? 'text-white/80' : 'text-[#9CA3AF]'}`}>· {p.travellerType}</span>}
          </p>
          <p className={`text-[11.5px] ${isSel ? 'text-white/80' : 'text-[#6B7280]'}`}>
            Passport: {p.passportNumber || <span className="italic">not given</span>}
            {booksOwnFlights(p) && <span className={`ml-1.5 font-semibold ${isSel ? 'text-white' : 'text-[#D97706]'}`}>· Booking own flights</span>}
          </p>
        </div>
        {inBox && (
          <button
            onClick={(e) => { e.stopPropagation(); place(null, p.id); }}
            className={`leading-none text-[16px] ${isSel ? 'text-white/80' : 'text-[#9CA3AF] hover:text-dr'}`}
            title="Take out of this box"
            aria-label={`Take ${fullName(p)} out of this box`}
          >×</button>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center text-[13px] text-[#9CA3AF]">Loading…</div>;
  }

  return (
    <div className="space-y-4" onClick={() => setSelected(null)}>
      {/* Header */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-[14.5px] font-bold text-[#111827]">Group Flights</h3>
            <p className="text-[12.5px] text-[#6B7280] mt-1 max-w-[560px]">
              Everyone who has completed the participation form appears below. Drag each person into Group Flights or
              Separate Flights, or tap them and then tap a box. Changes save automatically.
            </p>
          </div>
          {participants.length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); exportFlightsSheet({ assignments, participants: sorted, request }); }}
              className="px-3 py-1.5 text-[12px] font-semibold text-[#374151] border border-[#E5E7EB] rounded-[8px] hover:bg-surface transition"
            >
              Export list
            </button>
          )}
        </div>
        {error && <p className="text-[12.5px] text-dr mt-3">{error}</p>}
      </div>

      {/* Not yet sorted */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5" {...dropProps(null)}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">Not sorted yet · {unsorted.length}</p>
          <p className="text-[11.5px] text-[#9CA3AF]">{participants.length} registered</p>
        </div>
        {participants.length === 0 ? (
          <p className="text-[13px] text-[#9CA3AF]">No one has completed the participation form yet.</p>
        ) : unsorted.length === 0 ? (
          <p className="text-[13px] text-sg font-semibold">Everyone has been sorted.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{unsorted.map((p) => card(p))}</div>
        )}
        {selectedP && (
          <p className="text-[12px] text-pb font-semibold mt-3">Now tap a box to place <strong>{fullName(selectedP)}</strong></p>
        )}
      </div>

      {/* The two boxes */}
      <div className="grid grid-cols-1 1080:grid-cols-2 gap-4">
        {FLIGHT_BOXES.map((box) => {
          const members = (assignments[box.id] || []).map((pid) => byId[pid]).filter(Boolean);
          const isTarget = !!selected && !(assignments[box.id] || []).includes(selected);
          return (
            <div
              key={box.id}
              {...dropProps(box.id)}
              onClick={(e) => { if (isTarget) { e.stopPropagation(); place(box.id); } }}
              className={`bg-white rounded-[14px] border-2 transition-all ${isTarget ? 'border-pb cursor-pointer shadow-md' : 'border-[#E5E7EB]'}`}
            >
              <div className="px-5 py-3 border-b border-[#F1F3F6] flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-bold text-[#111827]">{box.name}</p>
                  <p className="text-[11px] text-[#9CA3AF]">{box.hint} · {members.length} {members.length === 1 ? 'person' : 'people'}</p>
                </div>
                {isTarget && <span className="text-[11px] font-bold text-pb">Tap to place</span>}
              </div>
              <div className="p-4 space-y-2 min-h-[80px]">
                {members.length === 0
                  ? <p className="text-[12px] text-[#D1D5DB] italic">Drop people here</p>
                  : members.map((p) => card(p, { inBox: true }))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
